import { Button } from "@/modern/components/ui/button";
import { FileText, Target, Users, Award, Info } from "lucide-react";
import { format } from "date-fns";
import { useContext, useState, useEffect } from "react";
import BryntumContext from "@/react/context/bryntumContext.jsx";
import { BaseModal } from "../../base/BaseModal.jsx";
import { BaseModalBody } from "../../base/BaseModalBody.jsx";
import { SWIVU_ASSIST_CONFIG } from "@/other/constants/OtherKeys.jsx";
import { SpotIncentiveFormData } from "./spotIncentiveSchema";
import SpotIncentiveImagePreview, { ImageThumbnail } from "./SpotIncentiveImagePreview";

interface SpotIncentivePreviewProps {
  data: SpotIncentiveFormData & { id?: number };
  editorInstance?: unknown;
  onClose?: () => void;
}

const SpotIncentivePreview = ({
  data,
  editorInstance,
  onClose
}: SpotIncentivePreviewProps) => {
  const { project } = useContext(BryntumContext) as any; // eslint-disable-line @typescript-eslint/no-explicit-any
  const [isOpen, setIsOpen] = useState(true);

  // Auto-open when component mounts
  useEffect(() => {
    setIsOpen(true);
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    if (onClose) {
      onClose();
    }
  };

  // Helper functions to get real data from Bryntum stores
  const getSelectedTask = (taskId: number | null) => {
    if (!taskId) return undefined;
    return project?.extraTaskStore?.getById(taskId);
  };

  const getSelectedResources = (resourceIds: number[]) => {
    return resourceIds.map(id => project?.resourceStore?.getById(id)).filter(Boolean);
  };

  const getTagName = (tagId: number) => {
    const tag = project?.incentiveTagsStore?.getById(tagId);
    return tag?.name || `Tag ${tagId}`;
  };

  const getTotalPoints = () => {
    const positivePoints = data.positiveTagIds.reduce((sum: number, tagId: number) => {
      const tag = project?.incentiveTagsStore?.getById(tagId);
      return sum + (tag?.categoryRecord?.points || 0);
    }, 0);

    const negativePoints = data.negativeTagIds.reduce((sum: number, tagId: number) => {
      const tag = project?.incentiveTagsStore?.getById(tagId);
      return sum + (tag?.categoryRecord?.points || 0);
    }, 0);

    return positivePoints - negativePoints;
  };


  const hasPointsAssigned = data.positiveTagIds.length > 0 || data.negativeTagIds.length > 0;
  const totalPoints = getTotalPoints();

  const MODAL_SIZE_CONFIG = {
    width: "min(90vw, 48rem)",
    height: "min(90vh, 47rem)",
    minWidth: "min(90vw, 48rem)",
    minHeight: "min(90vh, 47rem)",
    maxWidth: "min(90vw, 55rem)",
    maxHeight: "min(90vh, 50rem)"
  };

  if (!isOpen) return null;

  return (
    <BaseModal
        title={data.name}
        description=""
        onClose={handleClose}
        size={MODAL_SIZE_CONFIG}
        editorInstance={editorInstance}
        record={data}
        headerIconCls=""
        modal={false}
        version=""
        className="!resize-none [&_.border-t]:!border-t-0"
        swivuAssistanceConfig={SWIVU_ASSIST_CONFIG.spotIncentive}
        footerRenderer={() => (
          <div className="flex justify-end gap-3 pt-3 flex-shrink-0 !border-t-0">
            <Button
              variant="default"
              onClick={handleClose}
            >
              Done
            </Button>
          </div>
        )}
      >
      <BaseModalBody>
        <div className="space-y-4 overflow-y-auto flex-1 pt-1 pb-4">
            {/* Multiply by Days Info */}
          <div className="flex items-center gap-2 rounded-lg border border-border bg-accent/50 px-3 py-2.5">
            <Info className="h-4 w-4 text-primary flex-shrink-0" />
            <span className="text-sm font-medium text-foreground">
              Multiply by days on task is {data.multiplyByDays ? "enabled" : "disabled"}
            </span>
          </div>
          {/* Task Section - What work */}
          {data.taskId && (
            <div className="bg-card rounded-lg border border-border p-4">
              <div className="flex items-center gap-2 mb-2.5">
                <Target className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold text-foreground">Task</h3>
              </div>
              <p className="text-sm font-medium text-foreground m-0">{getSelectedTask(data.taskId)?.name}</p>
            </div>
          )}

          {/* Resources Section - Who did it */}
          {data.resourceIds.length > 0 && (
            <div className="bg-card rounded-lg border border-border p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <h3 className="text-sm font-semibold text-foreground">Resources ({data.resourceIds.length})</h3>
                </div>
                <span className="text-xs font-medium text-muted-foreground">
                  {data.startDate && data.endDate && (
                    <>
                      {format(new Date(data.startDate), "MMM dd")} - {format(new Date(data.endDate), "MMM dd, yyyy")}
                    </>
                  )}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {getSelectedResources(data.resourceIds).map((resource) => (
                  <div key={resource.id} className="rounded-md p-2.5 bg-secondary/50 border border-border">
                    <p className="text-sm font-medium text-foreground m-0">{resource.name}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* File Evidence Section */}
          {data.fileId && (() => {
            const fileRecord = project?.boardFilesStore?.getById(data.fileId);
            if (!fileRecord) return null;

            return (
              <SpotIncentiveImagePreview
                images={[{
                  src: fileRecord.url,
                  alt: fileRecord.name || 'Evidence image',
                  caption: fileRecord.description
                }]}
                allowZoom={true}
              >
                <div className="bg-card rounded-lg border border-border p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <h3 className="text-sm font-semibold text-foreground">Evidence</h3>
                  </div>
                  <div className="flex gap-3">
                    {fileRecord.url && (
                      <ImageThumbnail
                        src={fileRecord.thumbnailUrl || fileRecord.url}
                        alt={fileRecord.name}
                        index={0}
                        className="w-20 h-20 rounded-lg overflow-hidden bg-muted flex-shrink-0 border border-border cursor-pointer hover:opacity-80 transition-opacity"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">{fileRecord.name}</p>
                      {fileRecord.description && (
                        <p className="text-sm text-muted-foreground mt-1">{fileRecord.description}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {fileRecord.formatFileSize} • {fileRecord.aging}
                      </p>
                    </div>
                  </div>
                </div>
              </SpotIncentiveImagePreview>
            );
          })()}

          {/* Points Outcome - The Reward */}
          {hasPointsAssigned && (
            <div className="bg-card rounded-lg border border-border p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Award className="h-4 w-4 text-muted-foreground" />
                  <h3 className="text-sm font-semibold text-foreground">Points Awarded</h3>
                </div>
                <div className={`flex items-center justify-center rounded-lg px-3 py-1.5 font-semibold text-base ${
                  totalPoints >= 0
                    ? 'bg-primary/10 text-primary'
                    : 'bg-destructive/10 text-destructive'
                }`}>
                  {totalPoints >= 0 ? "+" : ""}{totalPoints}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                {/* Positive Tags */}
                {data.positiveTagIds.map((tagId) => {
                  const tag = project?.incentiveTagsStore?.getById(tagId);
                  const points = tag?.categoryRecord?.points || 0;
                  return (
                    <div key={`positive-${tagId}`}
                    className="flex items-start justify-between rounded-lg p-2.5 bg-green-600/5 dark:bg-green-500/10 border border-green-600/20 dark:border-green-500/20 gap-2">
                      <span className="text-sm font-medium text-foreground flex-1 min-w-0 break-words">{getTagName(tagId)}</span>
                      <span className="text-sm font-semibold text-green-600 dark:text-green-500 flex-shrink-0">+{points} pts</span>
                    </div>
                  );
                })}
                {/* Negative Tags */}
                {data.negativeTagIds.map((tagId) => {
                  const tag = project?.incentiveTagsStore?.getById(tagId);
                  const points = tag?.categoryRecord?.points || 0;
                  return (
                    <div key={`negative-${tagId}`}
                         className="flex items-start justify-between rounded-lg p-2.5 bg-destructive/5 dark:bg-destructive/10 border border-destructive/20 gap-2">
                      <span className="text-sm font-medium text-foreground flex-1 min-w-0 break-words">{getTagName(tagId)}</span>
                      <span className="text-sm font-semibold text-destructive flex-shrink-0">-{points} pts</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </BaseModalBody>
      </BaseModal>
  );
};

export default SpotIncentivePreview;
